import axios, { AxiosError } from 'axios'
import { API_URL, BOLT_BACKEND_URL, CMS_URL } from 'constants/envs'
import { getAccessToken, getActiveAccessToken, getRefreshToken, setTokens } from './localStorageService'
import { getUserTypeLoginPage } from 'utils/user'
import { getRefreshTokenUrlForUserType, logout, logoutMagicSession } from './app'
import { getUserType, identifyUserType } from 'utils/userType'
import { UserTypes } from 'store/auth/types'
import router from 'router/routing.tsx'
import { RefreshTokensResponse } from 'store/queries/bolt-api/types.ts'
import { Response } from 'types/request.ts'
import { STORAGE_TYPES } from 'types/auth.ts'
import { addLogBreadcrumb, log } from 'utils/logging.ts'
import { Criticality, Severity } from 'types/logging.ts'
import dayjs from 'dayjs'
import { Token } from '@boltenergy-be/shared'
import { MAGIC_SESSION_EXPIRED_CODE, MAGIC_TOKEN_EXCHANGE_PATH } from 'constants/auth.ts'

declare module 'axios' {
  export interface AxiosRequestConfig {
    // Id of the access token this request was authenticated with, so the response interceptor can tell a 401 caused
    // by a since-rotated token apart from a genuine failure of the current session
    _authTokenId?: string
    _retry?: boolean
  }
}

let isRefreshing = false
let failedQueue: any[] = []

const resolveUserType = (): UserTypes => getUserType() || identifyUserType()

const processQueue = (error: Error | null, token: Token | null = null) => {
  failedQueue.forEach((prom) => {
    if (error) {
      prom.reject(error)
    } else {
      prom.resolve(token)
    }
  })

  failedQueue = []
}

/**
 * Returns the correct redirect URL for the given userType
 * @param {UserTypes} userType
 */
export const handleLogout = (userType: UserTypes) => {
  logout(() => void router.navigate(getUserTypeLoginPage(userType)), userType)
}

/**
 * Ends an expired magic-link session for this tab (sessionStorage only) and redirects to the login page.
 * @param {UserTypes} userType
 */
export const handleMagicSessionLogout = (userType: UserTypes) => {
  logoutMagicSession(() => void router.navigate(getUserTypeLoginPage(userType)), userType)
}

// Request interceptor
axios.interceptors.request.use(
  (config) => {
    if (config.baseURL !== BOLT_BACKEND_URL) {
      config.baseURL = API_URL
    }

    // Set necessary headers
    config.headers['Content-Type'] = 'application/json'
    config.headers['Accept'] = 'application/json'

    // App debugging - axios request
    // if (!IS_PRODUCTION && Capacitor.isNativePlatform()) {
    //   console.log(`AXIOS(${config.baseURL})--request:`, JSON.stringify(config, null, 2))
    // }

    // The magic-token exchange is an anonymous endpoint: never attach auth state to it, and don't let a leftover
    // (expired) magic-link session block exchanging a new magic token.
    if (config.url?.includes(MAGIC_TOKEN_EXCHANGE_PATH)) {
      return config
    }

    // A magic-link (sessionStorage) token is the active token whenever it is present, even when a localStorage login
    // session also exists. It has no refresh path, so once it expires we end the magic session for this tab instead of
    // falling back to (and refreshing) the localStorage login token, which may belong to a different user.
    // Resolve the user type the same way the response interceptor does. Reading the token without one leaves the request
    // unauthenticated whenever the store has not hydrated yet, which then surfaces as an unexplained 401.
    const activeAccessToken = getActiveAccessToken(resolveUserType())
    if (activeAccessToken?.source === STORAGE_TYPES.sessionStorage) {
      const now = dayjs().unix()
      if (activeAccessToken.token.expiresAt && activeAccessToken.token.expiresAt < now) {
        // Session tokens are currently only for customers.
        handleMagicSessionLogout(UserTypes.CUSTOMER)
        throw Object.assign(new Error('Magic-link session access token expired, logging out and not calling endpoint.'), {
          code: MAGIC_SESSION_EXPIRED_CODE
        })
      }
    }

    if (activeAccessToken && !config.url?.includes('maps.googleapis.com') && !config.url?.includes(CMS_URL || 'localhost:1337')) {
      config.headers['Authorization'] = 'Bearer ' + activeAccessToken.token.id
      config._authTokenId = activeAccessToken.token.id
    }

    return config
  },
  async (error) => {
    addLogBreadcrumb('Request error (axios, l68)', { error })
    return Promise.reject(error)
  }
)

// Response interceptor
axios.interceptors.response.use(
  (response) => {
    // App debugging - axios response
    // if (!IS_PRODUCTION && Capacitor.isNativePlatform()) {
    //   console.log(`AXIOS--response(success):`, JSON.stringify(response, null, 2))
    // }

    return response
  },
  async (error: AxiosError) => {
    const originalRequest = { ...error.config, _retry: error.config?._retry || false }
    const userType = resolveUserType()
    const refreshTokenUrl = getRefreshTokenUrlForUserType(userType)

    // App debugging - axios response
    // if (!IS_PRODUCTION && Capacitor.isNativePlatform()) {
    //   console.log(`AXIOS--response(error):`, JSON.stringify(error, null, 2))
    // }

    if (!originalRequest) {
      return Promise.reject(error)
    }

    // A failed magic token exchange is handed by the caller in ProtectedRoute.tsx
    if (originalRequest?.url?.includes(MAGIC_TOKEN_EXCHANGE_PATH)) {
      return Promise.reject(error)
    }

    // If the refresh token call has failed, log out
    if (originalRequest?.url === `${API_URL}${refreshTokenUrl}`) {
      handleLogout(userType)
      return Promise.reject(error)
    }

    // If the error is a validation error, always log it
    if (error.response?.status === 422) {
      const url = error.request?.url || error.request?.responseURL
      log({
        error: `Validation error${url ? ` for ${url}` : ''}`,
        identifier: '[BACKEND:validation]',
        severity: Severity.ERROR,
        criticality: Criticality.CRITICAL,
        metaData: {
          error: {
            data: error.response ? JSON.stringify(error.response, null, 2) : null
          }
        }
      })
    }

    // If the error is "unauthorized" and there hasn't been a retry yet -> Handle refresh and try again.
    if (error.response?.status === 401 && !originalRequest?._retry) {
      // Parse data if necessary
      if (!!originalRequest?.data && typeof originalRequest.data === 'string') {
        originalRequest.data = JSON.parse(originalRequest.data)
      }

      const activeAccessToken = getActiveAccessToken(userType)

      // The tokens were rotated (e.g. a magic-token exchange) while this request was in flight: the 401 belongs to the
      // old token, not the current session. Retry once; the request interceptor attaches the current token.
      if (activeAccessToken && error.config?._authTokenId !== activeAccessToken.token.id) {
        originalRequest._retry = true
        return axios(originalRequest)
      }

      // If the request was authenticated with a magic-link (sessionStorage) token, there is no refresh path.
      // Never fall back to the localStorage refresh token (it may belong to a different user)
      // -> end the magic session instead.
      if (activeAccessToken?.source === STORAGE_TYPES.sessionStorage) {
        handleMagicSessionLogout(userType)
        addLogBreadcrumb('Magic-link session expired on 401, logging out', { error })
        return Promise.reject(error)
      }

      // If already refreshing, queue other requests until refresh is finalized.
      if (isRefreshing) {
        return new Promise<Token>((resolve, reject) => {
          failedQueue.push({ resolve, reject })
        })
          .then((token) => {
            if (originalRequest?.headers) {
              originalRequest.headers['Authorization'] = `Bearer ${token.id}`
            }

            return axios(originalRequest)
          })
          .catch((err: unknown) => Promise.reject(err))
      }

      originalRequest._retry = true

      // If refresh token is present:
      // 1. fetch new tokens with the refresh token
      // 2. save the new tokens if present (2.1) and process the failed queue with the these tokens (2.2).
      // 3. Logout if no tokens were return or the endpoint failed.
      const refreshToken = getRefreshToken(userType)
      if (refreshToken) {
        isRefreshing = true

        return (
          axios
            // 1.
            .post(`${API_URL}${refreshTokenUrl}`, {
              refreshToken: refreshToken.id
            })
            .then(({ status, data: responseData }) => {
              const { data } = responseData as Response<RefreshTokensResponse>

              if (status === 200 && data?.tokens) {
                const { token, refreshToken } = data.tokens
                // 2.1
                setTokens({ token, refreshToken }, userType)

                axios.defaults.headers.common['Authorization'] = 'Bearer ' + getAccessToken(userType)?.id

                // 2.2
                processQueue(null, token)

                return axios(originalRequest)
              }

              // 3.
              handleLogout(userType)
              const error = new Error('Failed to refresh token')
              addLogBreadcrumb('Failed to refresh token, logging out', { error })
              return Promise.reject(error)
            })
            .catch((err: unknown) => {
              processQueue(err instanceof Error ? err : new Error(String(err)), null)
              return Promise.reject(err)
            })
            .finally(() => {
              isRefreshing = false
            })
        )
      }

      // Without a refresh token the session cannot be recovered. End the session, the same way the RTK Query re-auth path already does.
      handleLogout(userType)
      addLogBreadcrumb('Unauthorized without a refresh token, logging out', { error })
    }

    return Promise.reject(error)
  }
)

export default axios
